import { useCallback, useEffect, useState } from "react";

import { WalletService } from "@/modules/dashboard/cards/services/walletsService";
import type { CardHoldingWallet } from "@/modules/dashboard/cards/types/wallets-api.types";
import { HttpApiService } from "@/shared/services/httpApi.service";

export interface UseCardHoldingWalletResult {
  wallet: CardHoldingWallet | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * The deposit address does not move, so the window is generous: this exists to pick up a change of pool, not
 * to track anything live.
 */
const STALE_AFTER_MS = 10 * 60_000;

let cached: { data: CardHoldingWallet; fetchedAt: number } | null = null;
let inflight: Promise<CardHoldingWallet> | null = null;

/** Drops the cached read so the next mount or refresh goes to the network. */
export function invalidateCardHoldingWallet(): void {
  cached = null;
}

function ensureFresh(force: boolean): Promise<CardHoldingWallet> {
  if (!force && cached && Date.now() - cached.fetchedAt < STALE_AFTER_MS) {
    return Promise.resolve(cached.data);
  }
  if (inflight) return inflight;

  inflight = WalletService.getCardHoldingWallet()
    .then((res) => {
      cached = { data: res.data, fetchedAt: Date.now() };
      return res.data;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Where a client deposits so Coloca can credit a card wallet: the network, the address and the assets that
 * address takes.
 *
 * Unkeyed — there is one pool for the whole card program, so every caller shares the same read and the same
 * cache. It is read live from the provider on the backend, which is why `enabled` exists: the dialog that
 * shows it asks only once it is actually open.
 */
export function useCardHoldingWallet(enabled: boolean): UseCardHoldingWalletResult {
  const [wallet, setWallet] = useState<CardHoldingWallet | null>(cached?.data ?? null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Derived from "a read was asked for and has not settled", so the effect never sets state synchronously.
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const isLoading = enabled && settledKey !== reloadKey;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    ensureFresh(reloadKey > 0)
      .then((data) => {
        if (cancelled) return;
        setWallet(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setSettledKey(reloadKey);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  const refresh = useCallback(() => {
    invalidateCardHoldingWallet();
    setReloadKey((key) => key + 1);
  }, []);

  return { wallet, isLoading, error, refresh };
}
